import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Autocomplete,
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TextField,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography
} from "@mui/material";
import { useApiFetch } from "../api/apiClient";
import { colors } from "../utils/styles";
import { fmtDateTime } from "../utils/dateFormat";
import { compareValues, isEmpty, naturalCompare } from "../utils/sort";
import { getSubmissionYear, yearOptionsFrom } from "../utils/submission";

const COLUMNS = [
  { key: "parentJurisdictionCode", label: "Parent Code" },
  { key: "parentJurisdictionName", label: "Parent Name" },
  { key: "jurisdictionCode", label: "Jurisdiction Code" },
  { key: "jurisdictionNameCurrent", label: "Taxsys Tax Bill Description" },
  { key: "jurisdictionName", label: "Tax Bill Description Submission" },
  { key: "newTaxBillDescription", label: "Requested Tax Bill Description" },
  { key: "submittedAt", label: "Submitted" },
  { key: "updatedBy", label: "Confirmed By" },
  { key: "notes", label: "Notes" }
];
// Distinct {code, name, label} options for one code/name column pair, sorted by label.
function codeOptions(rows, codeKey, nameKey, include) {
  const seen = new Map();
  for (const r of rows) {
    const code = r[codeKey];
    if (!code || seen.has(code) || (include && !include(r))) continue;
    const name = r[nameKey] || null;
    seen.set(code, { code, name, label: name ? `${name} (${code})` : code });
  }
  return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
}

export default function DescriptionRequest() {
  const apiFetch = useApiFetch();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [savingId, setSavingId] = useState(null);
  const [sortBy, setSortBy] = useState("parentJurisdictionCode");
  const [sortDir, setSortDir] = useState("asc");
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [yearFilters, setYearFilters] = useState([]);
  const [parentFilters, setParentFilters] = useState([]);
  const [jurisdictionFilters, setJurisdictionFilters] = useState([]);

  useEffect(() => {
    let cancelled = false;
    apiFetch("/admin/description-request?all=true")
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setRows(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to load description requests.");
      });

  return () => {
      cancelled = true;
    };
  }, [apiFetch]);

  const startEdit = (row) => {
    setEditingId(row.subagencyId);
    setEditValue(row.notes || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const saveNotes = async (row) => {
    if (savingId === row.subagencyId) return;
    if (editingId !== row.subagencyId) return;  // Guard against race condition
    if (editValue.length > 500) {
      setError("Notes cannot exceed 500 characters.");
      return;
    }

    setSavingId(row.subagencyId);
    try {
      const res = await apiFetch(`/admin/description-request/${row.subagencyId}/notes`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notes: editValue })
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }
      const updated = await res.json();
      setRows((prev) =>
        prev ? prev.map((r) => (r.subagencyId === updated.subagencyId ? updated : r)) : prev
      );
      setEditingId(null);
      setEditValue("");
    } catch (err) {
      setError(err.message || "Failed to update notes.");
    } finally {
      setSavingId(null);
    }
  };

  const yearOptions = useMemo(
    () => yearOptionsFrom((rows || []).map((r) => getSubmissionYear(r.submissionIdentifier))),
    [rows]
  );

  const parentOptions = useMemo(
    () => codeOptions(rows || [], "parentJurisdictionCode", "parentJurisdictionName"),
    [rows]
  );

  // Jurisdictions narrow to the selected parent(s).
  const jurisdictionOptions = useMemo(() => {
    const parentCodes = parentFilters.length ? new Set(parentFilters.map((p) => p.code)) : null;
    return codeOptions(
      rows || [],
      "jurisdictionCode",
      "jurisdictionName",
      parentCodes ? (r) => parentCodes.has(r.parentJurisdictionCode) : null
    );
  }, [rows, parentFilters]);

  // Drops any jurisdiction selections that no longer belong to the selected
  // parent set, without re-deriving eligibility separately from jurisdictionOptions.
  useEffect(() => {
    setJurisdictionFilters((prev) => {
      if (prev.length === 0) return prev;
      const validCodes = new Set(jurisdictionOptions.map((o) => o.code));
      const next = prev.filter((jf) => validCodes.has(jf.code));
      return next.length === prev.length ? prev : next;
    });
  }, [jurisdictionOptions]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    if (!yearFilters.length && !parentFilters.length && !jurisdictionFilters.length) return rows;
    const yearSet = yearFilters.length ? new Set(yearFilters) : null;
    const parentSet = parentFilters.length ? new Set(parentFilters.map((p) => p.code)) : null;
    const jurisdictionSet = jurisdictionFilters.length ? new Set(jurisdictionFilters.map((j) => j.code)) : null;
    return rows.filter((r) => {
      if (yearSet && !yearSet.has(getSubmissionYear(r.submissionIdentifier))) return false;
      if (parentSet && !parentSet.has(r.parentJurisdictionCode)) return false;
      if (jurisdictionSet && !jurisdictionSet.has(r.jurisdictionCode)) return false;
      return true;
    });
  }, [rows, yearFilters, parentFilters, jurisdictionFilters]);

  const sorted = useMemo(() => {
    if (!sortBy) return filtered;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      // Two empty primaries are a true tie — leave the order alone rather than
      // falling through to the code tie-breakers.
      if (isEmpty(av) && isEmpty(bv)) return 0;

      const cmp = compareValues(av, bv, dir);
      if (cmp !== 0) return cmp;

      // Primary values tie: break by Parent Code, then Jurisdiction Code.
      if (sortBy !== "parentJurisdictionCode") {
        const parentCmp = naturalCompare(
          String(a.parentJurisdictionCode || ""),
          String(b.parentJurisdictionCode || "")
        );
        if (parentCmp !== 0) return parentCmp;
      }
      if (sortBy !== "jurisdictionCode") {
        return naturalCompare(
          String(a.jurisdictionCode || ""),
          String(b.jurisdictionCode || "")
        );
      }
      return cmp;
    });
  }, [filtered, sortBy, sortDir]);

  const pagedRows = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir("asc");
    }
    setPage(0);
  };

  const onToggleConfirmed = async (row) => {
    if (savingId === row.subagencyId) return;
    setSavingId(row.subagencyId);
    try {
      const res = await apiFetch(`/admin/description-request/${row.subagencyId}/confirm`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updateConfirmed: !row.updateConfirmed })
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }
      const updated = await res.json();
      setRows((prev) =>
        prev ? prev.map((r) => (r.subagencyId === updated.subagencyId ? updated : r)) : prev
      );
    } catch (err) {
      setError(err.message || "Failed to update confirmation.");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Stack direction="row" sx={{ alignItems: "center", mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>
          Tax Bill Description Request
        </Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!error && rows === null && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {rows && rows.length === 0 && (
        <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>No submissions yet.</Box>
      )}

      {rows && rows.length > 0 && (
        <>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ mb: 2, alignItems: { sm: "center" } }}
          >
            <Autocomplete
              multiple
              disableCloseOnSelect
              limitTags={1}
              options={yearOptions}
              value={yearFilters}
              onChange={(_, v) => {
                setYearFilters(v);
                setPage(0);
              }}
              sx={{ minWidth: 200, flex: 0 }}
              renderInput={(params) => <TextField {...params} label="Year" size="small" />}
            />
            <Autocomplete
              multiple
              disableCloseOnSelect
              limitTags={1}
              options={parentOptions}
              value={parentFilters}
              onChange={(_, v) => {
                setParentFilters(v);
                setPage(0);
              }}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              sx={{ minWidth: 320, flex: 1 }}
              renderInput={(params) => <TextField {...params} label="Agency/Group" size="small" />}
            />
            <Autocomplete
              multiple
              disableCloseOnSelect
              limitTags={1}
              options={jurisdictionOptions}
              value={jurisdictionFilters}
              onChange={(_, v) => {
                setJurisdictionFilters(v);
                setPage(0);
              }}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              sx={{ minWidth: 320, flex: 1 }}
              renderInput={(params) => <TextField {...params} label="Jurisdiction" size="small" />}
            />
            {(yearFilters.length > 0 || parentFilters.length > 0 || jurisdictionFilters.length > 0) && (
              <Button
                onClick={() => {
                  setYearFilters([]);
                  setParentFilters([]);
                  setJurisdictionFilters([]);
                  setPage(0);
                }}
              >
                Clear filters
              </Button>
            )}
          </Stack>

          {(yearFilters.length > 0 || parentFilters.length > 0 || jurisdictionFilters.length > 0) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Showing {sorted.length} of {rows.length} submission{rows.length === 1 ? "" : "s"}.
            </Typography>
          )}

          <TableContainer component={Paper}>
            <Table size="small" stickyHeader>
              <TableHead
                sx={{
                  "& .MuiTableCell-head": {
                    bgcolor: colors.darkGray,
                    color: "white",
                    fontWeight: 700,
                    whiteSpace: "nowrap"
                  },
                  "& .MuiTableSortLabel-root": { color: "white !important" },
                  "& .MuiTableSortLabel-root.Mui-active": { color: "white !important" },
                  "& .MuiTableSortLabel-icon": { color: "white !important" }
                }}
              >
                <TableRow>
                  <TableCell padding="checkbox">Confirmed</TableCell>
                  {COLUMNS.map((c) => (
                    <TableCell key={c.key} sortDirection={sortBy === c.key ? sortDir : false}>
                      <TableSortLabel
                        active={sortBy === c.key}
                        direction={sortBy === c.key ? sortDir : "asc"}
                        onClick={() => toggleSort(c.key)}
                      >
                        {c.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.map((r) => (
                  <TableRow key={r.subagencyId} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={!!r.updateConfirmed}
                        disabled={savingId === r.subagencyId}
                        onChange={() => onToggleConfirmed(r)}
                        inputProps={{ "aria-label": `confirm ${r.jurisdictionCode}` }}
                      />
                    </TableCell>
                    <TableCell>{r.parentJurisdictionCode || "—"}</TableCell>
                    <TableCell>{r.parentJurisdictionName || "—"}</TableCell>
                    <TableCell>{r.jurisdictionCode || "—"}</TableCell>
                    <TableCell>{r.jurisdictionNameCurrent || "—"}</TableCell>
                    <TableCell>{r.jurisdictionName || "—"}</TableCell>
                    <TableCell sx={{ maxWidth: 360, whiteSpace: "normal" }}>
                      {r.newTaxBillDescription || "—"}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={r.submissionIdentifier} arrow>
                        <span>{fmtDateTime(r.submittedAt)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {r.updateConfirmed && r.updatedBy ? (
                        <Tooltip title={`${r.updatedBy} — ${fmtDateTime(r.updatedAt)}`} arrow>
                          <span>{r.updatedByName || r.updatedBy}</span>
                        </Tooltip>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      {editingId === r.subagencyId ? (
                        <TextField
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              saveNotes(r);
                            } else if (e.key === "Escape") {
                              cancelEdit();
                            }
                          }}
                          onBlur={() => editingId === r.subagencyId && saveNotes(r)}
                          fullWidth
                          multiline
                          maxRows={3}
                          size="small"
                          disabled={savingId === r.subagencyId}
                          helperText={`${editValue.length}/500${editValue.length > 500 ? " (too long)" : ""}`}
                          error={editValue.length > 500}
                          autoFocus
                          sx={{ fontSize: "0.875rem" }}
                        />
                      ) : (
                        <Box
                          onClick={() => startEdit(r)}
                          sx={{
                            cursor: "pointer",
                            minHeight: "1.5rem",
                            padding: "4px",
                            borderRadius: "4px",
                            "&:hover": { bgcolor: "action.hover" },
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word"
                          }}
                        >
                          {r.notes || "—"}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            rowsPerPageOptions={[10, 25, 50, 100]}
            count={sorted.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </>
      )}
    </Box>
  );
}
